import { normalizeMatrixRows, stringifyMatrixRows } from '@/lib/zymatrix-utils';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ZyMatrixNodeView from './zymatrix-node-view';

function getMatrixTextFromElement(element: HTMLElement): string {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('zyLabel').forEach(labelElement => labelElement.remove());
    return clone.textContent?.trim() || '';
}

function getMergedLabelFromElement(element: HTMLElement): string {
    const labelAttr = element.getAttribute('label') || '';
    const labelNodeText = element.querySelector('zyLabel')?.textContent || '';
    return `${labelAttr}${labelNodeText}`;
}

export const ZyMatrixHeadless = Node.create({
    name: 'zyMatrix',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            label: {
                default: '',
                parseHTML: element => getMergedLabelFromElement(element as HTMLElement),
                renderHTML: attributes => ({
                    label: attributes.label,
                }),
            },
            super: {
                default: '',
                parseHTML: element => element.getAttribute('super') || '',
                renderHTML: attributes => ({
                    super: attributes.super,
                }),
            },
            center: {
                default: false,
                parseHTML: element => element.getAttribute('center') === 'true',
                renderHTML: attributes => ({
                    center: attributes.center ? 'true' : 'false',
                }),
            },
            matrix: {
                default: [],
                parseHTML: element => normalizeMatrixRows(getMatrixTextFromElement(element as HTMLElement)),
                renderHTML: () => ({}),
            },
            legacyHtml: {
                default: null,
                parseHTML: element => element.getAttribute('legacy-html'),
                renderHTML: attributes => ({
                    ...(attributes.legacyHtml ? { 'legacy-html': attributes.legacyHtml } : {}),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'zyMatrix',
            },
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        const matrix = stringifyMatrixRows(node.attrs.matrix);
        return ['zyMatrix', mergeAttributes(HTMLAttributes), matrix];
    },
});

export const ZyMatrix = ZyMatrixHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyMatrixNodeView);
    },
});

export default ZyMatrix;
